import { useEffect, useState } from 'react'
import { api } from '../api'
import RinaContextCard from '../components/RinaContextCard'
import { MesTournees, HistoriqueTournees } from '../components/paniers/prod/MesTournees'
import MesOffresPaniers from '../components/paniers/prod/MesOffresPaniers'
import TourneeProd from '../components/paniers/prod/TourneeProd'
import { EtapeDispos, EtapeDemande, EtapePreparation, EtapeLivraison, EtapeArgent } from '../components/paniers/prod/EtapesProd'
import { ouvrirProfil } from '../components/ProfileSheet'
import FondPage from '../components/FondPage'
import EnteteModule from '../components/EnteteModule'
import BarreOnglets from '../components/BarreOnglets'
import { useTheme } from '../theme'
import { useActionsPortail } from '../contexts/navigation'
import { avecAlpha } from '../lib/couleurs'

// Module Paniers — facette PRODUCTEUR en ECRAN PLEIN (proto v7.3, paliers 1-2) :
// la tournee vecue comme une TIMELINE de taches, saisie des dispos depuis
// l'offre generale (stock partage). Navigation contextuelle dans la ligne du
// header (patron v7.2.5) : ← Lyannaj (racine) / ← Tournées (detail)
// / ← Tournée (ecran d'etape). Tabbar : Tournées / Mes offres / Historique.

const TEAL = 'rgb(var(--c-teal))'
const VERT = 'rgb(var(--c-green))'

const TITRES_ETAPE = {
  dispos: 'Mes disponibilités',
  demande: 'La demande',
  preparation: 'Ma préparation',
  livraison: 'La livraison',
  argent: 'Mon argent',
}

export default function PaniersProducerModule() {
  // Route sous /producteur (ADR-068) : la casquette est garantie par ZoneProducteur.
  const { fermerModule, handleRinaClick } = useActionsPortail()
  const onExit = () => fermerModule()
  const onGoOffers = () => { fermerModule('offers'); window.scrollTo(0, 0) }
  const { isWeb3 } = useTheme()
  const tc = 'text-ink'
  const glass = 'panneau-verre'

  const [tours, setTours] = useState([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('tournees') // tournees | offres | historique
  const [tourId, setTourId] = useState(null) // detail timeline ouvert
  const [etape, setEtape] = useState(null) // ecran d'etape ouvert (scr)
  const [flash, setFlash] = useState(null)
  const toast = (msg) => {
    setFlash(msg)
    setTimeout(() => setFlash(null), 2800)
  }

  // Recharge aussi apres une saisie de dispos (agregats de la liste/timeline)
  const rechargerTours = () =>
    api.producer.getTours()
      .then((t) => setTours(Array.isArray(t) ? t : []))
      .catch(() => {})

  useEffect(() => {
    rechargerTours().finally(() => setLoading(false))
  }, [])

  const tour = tours.find((t) => t.id === tourId) || null

  // Message RINA contextuel par ecran
  const rina = etape
    ? {
        dispos: 'Choisissez vos produits et vos quantités — un seul stock, partagé entre vos canaux. Ajustable jusqu’à la clôture.',
        demande: 'La demande en direct : ce que les clients réservent sur vos disponibilités.',
        preparation: 'Votre liste de préparation — les quantités vendues, à préparer pour la collecte.',
        livraison: 'Manrina collecte et livre. Rien à faire de votre côté.',
        argent: 'Votre argent, étape par étape : en séquestre pendant la fenêtre, puis versé ou crédité selon votre choix.',
      }[etape]
    : tourId
      ? 'La timeline de votre tournée — une seule étape « à faire » à la fois. Touchez une étape pour le détail.'
      : tab === 'historique'
        ? 'Vos tournées soldées et leurs pièces. Touchez-en une pour revoir ses étapes.'
        : tab === 'offres'
          ? 'Ce que vous proposez en Paniers — piloté par vos offres, un seul stock. La création reste dans Mes offres.'
          : 'Où en est chaque tournée, et ce que vous avez à faire. Je vous guide étape par étape.'

  // Retour contextuel : etape → tournee → liste → hub
  const back = etape
    ? { label: 'Tournée', fn: () => setEtape(null) }
    : tourId
      ? { label: 'Tournées', fn: () => setTourId(null) }
      : { label: 'Lyannaj', fn: onExit }

  const tabs = [
    { k: 'tournees', e: '🚚', l: 'Tournées' },
    { k: 'offres', e: '🏷️', l: 'Mes offres' },
    { k: 'historique', e: '🧾', l: 'Historique' },
  ]

  return (
    <FondPage halos>

      <div className="pb-28 max-w-md mx-auto relative z-[2]">
        <EnteteModule retour={{ label: back.label, onClick: back.fn }} titre="🧺 Paniers">
          <button
            onClick={ouvrirProfil}
            className={`flex items-center gap-1.5 rounded-full pl-2 pr-3 py-1.5 flex-shrink-0 ${glass}`}
            style={{ border: `1.5px solid ${TEAL}` }}
          >
            <span className="w-7 h-7 rounded-full flex items-center justify-center text-sm" style={{ background: avecAlpha(TEAL, 0.2) }}>🌱</span>
            <span className="text-xs font-bold" style={{ color: isWeb3 ? '#fff' : VERT }}>Producteur</span>
            <span className="text-[9px]" style={{ color: 'rgb(var(--c-ink) / 0.5)' }}>▾</span>
          </button>
        </EnteteModule>

        <div className="px-4 pt-1">
          <RinaContextCard title={etape ? TITRES_ETAPE[etape] : undefined} message={rina} />

          {/* Ecran d'etape ouvert */}
          {etape && tour ? (
            <>
              {etape === 'dispos' && <EtapeDispos tourId={tour.id} t={tour} onToast={toast} onChanged={rechargerTours} />}
              {etape === 'demande' && <EtapeDemande tourId={tour.id} />}
              {etape === 'preparation' && <EtapePreparation tourId={tour.id} />}
              {etape === 'livraison' && <EtapeLivraison t={tour} />}
              {etape === 'argent' && <EtapeArgent tourId={tour.id} onToast={toast} />}
            </>
          ) : tourId && tour ? (
            <TourneeProd t={tour} onOpenEtape={(scr) => setEtape(scr)} />
          ) : tab === 'historique' ? (
            <HistoriqueTournees tours={tours} loading={loading} onOpen={(id) => { setTourId(id); setEtape(null) }} />
          ) : tab === 'offres' ? (
            <MesOffresPaniers onToast={toast} onGoOffers={onGoOffers} />
          ) : (
            <MesTournees tours={tours} loading={loading} onOpen={(id) => { setTourId(id); setEtape(null) }} />
          )}
        </div>
      </div>

      {/* Tabbar producteur (proto v7.3 : Tournées / Mes offres / Historique) */}
      <BarreOnglets onglets={tabs} actif={tab} onSelect={(k) => { setTab(k); setTourId(null); setEtape(null) }} />

      {flash && (
        <div
          className="glass-dark fixed left-1/2 -translate-x-1/2 rounded-[14px] text-[13px] font-semibold text-center z-[80]"
          style={{ bottom: 90, padding: '11px 18px', maxWidth: 340, color: 'rgb(var(--c-creme))' }}
        >
          {flash}
        </div>
      )}
    </FondPage>
  )
}

